import { useEffect, useState } from "react";

/**
 * Viewport width alone, coalesced to one update per frame.
 *
 * `useWindowSize` returns a fresh `{ width, height }` object on every resize
 * event, so a consumer that only cares about width still re-renders when the
 * height changes by itself -- a mobile URL bar collapsing, or the on-screen
 * keyboard opening. For a provider whose value fans out to a whole page, that
 * is a re-render of the page for a change it cannot observe.
 */
export default function useViewportWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    let frame = 0;
    const handleResize = () => {
      frame ||= requestAnimationFrame(() => {
        frame = 0;
        // `setState` with an unchanged number bails out of the re-render, so a
        // height-only resize costs nothing beyond the frame it was read in.
        setWidth(window.innerWidth);
      });
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return width;
}
